import { useEffect } from "react"

let beaconScriptPromise

const loadBeaconScript = () => {
  if (beaconScriptPromise) return beaconScriptPromise

  if (!window.Beacon) {
    window.Beacon = (method, options, data) => {
      window.Beacon.readyQueue.push({ method, options, data })
    }
    window.Beacon.readyQueue = []
  }

  beaconScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script")
    script.src = "https://beacon-v2.helpscout.net"
    script.async = true
    script.setAttribute("fetchpriority", "low")
    script.onload = resolve
    script.onerror = () => {
      script.remove()
      beaconScriptPromise = null
      reject(new Error("Failed to load Help Scout Beacon"))
    }
    document.body.appendChild(script)
  })

  return beaconScriptPromise
}

const useBeaconWidget = beaconId => {
  useEffect(() => {
    let isMounted = true
    let initialized = false
    let activated = false
    let activationTimer
    let scrollPosition = window.scrollY

    const onScroll = () => {
      if (document.body.style.position !== "fixed") {
        scrollPosition = window.scrollY
      }
    }

    const onWidgetClose = () => {
      if (!window.matchMedia("(hover: hover)").matches) {
        document.body.style.position = ""
        document.body.style.top = ""
        window.scrollTo(0, scrollPosition)
      }
    }

    const observer = new MutationObserver(() => {
      if (
        document.body.style.position === "fixed" &&
        document.body.style.top === "0px"
      ) {
        document.body.style.top = `-${scrollPosition}px`
      }
    })

    const activate = () => {
      if (activated) return
      activated = true
      window.clearTimeout(activationTimer)
      removeActivationListeners()

      loadBeaconScript()
        .then(() => {
          if (!isMounted) return

          scrollPosition = window.scrollY
          observer.observe(document.body, {
            attributes: true,
            attributeFilter: ["style"],
          })
          window.addEventListener("scroll", onScroll, { passive: true })
          window.Beacon("init", beaconId)
          window.Beacon("on", "close", onWidgetClose)
          initialized = true
        })
        .catch(error => {
          observer.disconnect()
          window.removeEventListener("scroll", onScroll)
          if (isMounted) {
            console.error(error)
            activated = false
            addActivationListeners()
          }
        })
    }

    function addActivationListeners() {
      window.addEventListener("pointerdown", activate, { passive: true })
      window.addEventListener("keydown", activate)
      window.addEventListener("wheel", activate, { passive: true })
    }

    function removeActivationListeners() {
      window.removeEventListener("pointerdown", activate)
      window.removeEventListener("keydown", activate)
      window.removeEventListener("wheel", activate)
    }

    addActivationListeners()
    activationTimer = window.setTimeout(activate, 5000)

    return () => {
      isMounted = false
      window.clearTimeout(activationTimer)
      removeActivationListeners()
      observer.disconnect()
      window.removeEventListener("scroll", onScroll)

      if (initialized && window.Beacon) {
        window.Beacon("off", "close", onWidgetClose)
        window.Beacon("destroy")
      }
    }
  }, [beaconId])
}

export default useBeaconWidget
